import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getHelper } from "../../general/apiHelper";
import { BASE_URL } from "../../general/constants";

export const fetchPromos = createAsyncThunk(
	'casino/fetchPromos',
	async ({ accommodation, profileId, coupLott }, { rejectWithValue }) => {
		try {
			const params = new URLSearchParams();
			if (accommodation) params.append('accommodationId', accommodation);
			if (profileId) params.append('profileId', profileId);
			if (coupLott) params.append('coupLott', coupLott);
			
			const response = await getHelper(
				`${BASE_URL}/loyaltyCard/logismos/getPromos?${params.toString()}`,
				{}
			);
			
			return response;
		} catch (error) {
			return rejectWithValue(error.message || 'Error fetching promos');
		}
	}
);

export const refresh = createAsyncThunk(
	'casino/refresh',
	async ({ accommodation, profileId }, { rejectWithValue }) => {
		try {
			const params = new URLSearchParams();
			if (accommodation) params.append('accommodationId', accommodation);
			if (profileId) params.append('profileId', profileId);
			
			const response = await getHelper(
				`${BASE_URL}/loyaltyCard/logismos/refresh?${params.toString()}`,
				{}
			);
			
			return response;
		} catch (error) {
			return rejectWithValue(error.message || 'Error fetching promos');
		}
	}
);

const initialState = {
	coupons: [],
	groupedLotteries: {},
	status: 'idle', // 'idle', 'loading', 'succeeded', 'failed'
	error: null,
};

const casinoSlice = createSlice({
	name: 'casino',
	initialState,
	reducers: {},
	extraReducers: (builder) => {
		builder
			.addCase(refresh.pending, (state) => {
				state.status = 'loading';
			})
			.addCase(refresh.rejected, (state, action) => {
				state.status = 'failed';
				state.error = action.payload;
			})
			.addCase(refresh.fulfilled, (state,action) => {
				state.status = 'succeeded';
				state.error = null;
			})
			.addCase(fetchPromos.pending, (state) => {
				state.status = 'loading';
			})
			.addCase(fetchPromos.rejected, (state, action) => {
				state.status = 'failed';
				state.error = action.payload;
			})
			.addCase(fetchPromos.fulfilled, (state, action) => {
				const promoCodeToFriendlyName = {
					0: "All Outlets",
					1: "Casino Reception",
					2: "La Terrasse",
					3: "Alfredo's/O Kipos",
					4: "Vergina Theater",
					5: "Regency Club Booth",
					6: "Other",
					7: "Other",
					8: "Other",
					9: "Other",
					10: "Other",
					11: "Main Floor Bars",
					12: "Other",
					13: "Cash desk",
					14: "Retail Shop",
					15: "Ian's Bar",
					99: "Other"
				};
				
				state.status = 'succeeded';
				state.error = null;
				
				const groupedLotteries = {}; // Lotteries will be grouped
				let flatCoupons = []; // Coupons will be stored as a flat list
				const groupedCoupons = {
					// These will be dynamically filled based on intented_redemption
					// Other: [], // More than one location
				};
				action.payload.forEach((promo) => {
					const { promo_id, coup_lott, promo_series_descr, actualdate_issued, intented_redemption,
					} = promo;
					
					if (coup_lott === 'C') {
						const addToGroup = (code) => {
							const numericCode = Number(code);
							const friendlyName = promoCodeToFriendlyName[numericCode] || "Other";
							if (!groupedCoupons[friendlyName]) {
								groupedCoupons[friendlyName] = [];
							}
							groupedCoupons[friendlyName].push(promo);
						};
						
						flatCoupons.push(promo);
						const redemptionStr = String(intented_redemption || "").trim();
						if (redemptionStr.includes(';')) {
							redemptionStr.split(';').forEach(code => {
								if (code.trim()) addToGroup(code.trim());
							});
						} else {
							if (redemptionStr) addToGroup(redemptionStr);
						}
					}
						
						// if (coup_lott === 'C') {
					// 	flatCoupons.push(promo);
					//
					// 	const redemptionStr = String(intented_redemption);
					// 	console.log(redemptionStr)
					// 	console.log(intented_redemption)
					// 	if (redemptionStr.includes(';')) {
					// 		// Multiple locations (e.g., "1,2") → put in 'other'
					// 		groupedCoupons.other.push(promo);
					// 	} else {
					// 		// Single location → group by number
					// 		const key = redemptionStr.trim();
					// 		if (!groupedCoupons[key]) {
					// 			groupedCoupons[key] = [];
					// 		}
					// 		groupedCoupons[key].push(promo);
					// 	}
					// }
					else if (coup_lott === 'L') {
						// Lotteries: Group by promo_series_descr and promo_id
						if (!groupedLotteries[promo_series_descr]) {
							groupedLotteries[promo_series_descr] = {};
						}
						if (!groupedLotteries[promo_series_descr][promo_id]) {
							groupedLotteries[promo_series_descr][promo_id] = [];
						}
						groupedLotteries[promo_series_descr][promo_id].push(promo);
					}
				});
				
				// Sort Coupons by `actualdate_issued` (Latest First)
				flatCoupons.sort((a, b) => new Date(b.actualdate_issued) - new Date(a.actualdate_issued));
				
				// Sort Lotteries in each Group by `actualdate_issued`
				Object.keys(groupedLotteries).forEach(series => {
					Object.keys(groupedLotteries[series]).forEach(promoId => {
						groupedLotteries[series][promoId].sort((a, b) =>
							new Date(b.actualdate_issued) - new Date(a.actualdate_issued)
						);
					});
				});
				
				// Sort each group in groupedCoupons by actualdate_issued (Latest First)
				Object.values(groupedCoupons).forEach(couponGroup => {
					couponGroup.sort((a, b) => new Date(b.actualdate_issued) - new Date(a.actualdate_issued));
				});
				
				state.groupedCoupons = groupedCoupons;
				state.coupons = flatCoupons; // Store sorted coupons as a flat array
				state.groupedLotteries = groupedLotteries; // Store sorted lotteries as a nested object
			});
	},
});

export const {} = casinoSlice.actions;
export default casinoSlice.reducer;
